import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { NotebookPen, Plus, Pencil, Trash2, Copy, Repeat, Search, MessageSquare, ExternalLink, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const pretty = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "";
const SECTIONS = [
  ["barre", "Barre"], ["warmup", "Warm-up"], ["center", "Center"],
  ["across_floor", "Across the Floor"], ["dance", "Dance"], ["cool_down", "Cool Down"],
];
const LEGACY = [["technique", "Technique / skill work"], ["choreography", "Choreography / production work"], ["activities", "Activities / combinations"]];
const emptySection = () => ({ text: "", skill_ids: [] });

function SkillPicker({ sectionKey, value, onChange, skills, levelName, levelStyle, classStyleId, classLevelId }) {
  const [q, setQ] = useState("");
  const selected = value || [];
  const byId = useMemo(() => Object.fromEntries(skills.map((s) => [s.id, s])), [skills]);
  const suggestions = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let list = skills.filter((s) => !selected.includes(s.id));
    if (ql) list = list.filter((s) => (s.name || "").toLowerCase().includes(ql));
    const rank = (s) => (classLevelId && s.level_id === classLevelId) ? 0 : (classStyleId && levelStyle[s.level_id] === classStyleId) ? 1 : 2;
    return list.sort((a, b) => rank(a) - rank(b) || (a.name || "").localeCompare(b.name || "")).slice(0, 8);
  }, [q, skills, selected, classLevelId, classStyleId, levelStyle]);
  const add = (id) => { onChange([...selected, id]); setQ(""); };
  const remove = (id) => onChange(selected.filter((x) => x !== id));
  return (
    <div>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
        <Input data-testid={`plan-skill-search-${sectionKey}`} className="h-9 pl-8 text-sm bg-white" placeholder="Search curriculum skills…" value={q} onChange={(e) => setQ(e.target.value)} />
        {q && suggestions.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-56 overflow-y-auto">
            {suggestions.map((s) => (
              <button type="button" key={s.id} data-testid={`plan-skill-option-${sectionKey}-${s.id}`} onClick={() => add(s.id)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-teal-light/50 flex items-center justify-between gap-2">
                <span className="truncate">{s.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{levelName[s.level_id] || ""}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map((id) => (
            <span key={id} data-testid={`plan-skill-chip-${sectionKey}-${id}`} className="inline-flex items-center gap-1 bg-teal-light text-teal-dark border border-teal/30 rounded-full px-2.5 py-0.5 text-xs font-semibold">
              {byId[id]?.name || "Skill"}
              <button type="button" data-testid={`plan-skill-remove-${sectionKey}-${id}`} onClick={() => remove(id)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LessonPlans() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [skills, setSkills] = useState([]);
  const [levels, setLevels] = useState([]);
  const [filters, setFilters] = useState({ class_id: "", q: "" });

  const empty = { class_id: "", lesson_date: "", title: "", goals: "", music: "", materials: "", notes: "", private_notes: "", links: [], sections: {} };
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [legacyView, setLegacyView] = useState({});
  const [editId, setEditId] = useState(null);
  const [view, setView] = useState(null);
  const [comment, setComment] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/lesson-plans", { params }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/curriculum/skills").then((r) => setSkills(r.data)).catch(() => {});
    api.get("/curriculum/levels").then((r) => setLevels(r.data)).catch(() => {});
  }, []);
  useEffect(load, [filters]);

  const levelStyle = useMemo(() => Object.fromEntries(levels.map((l) => [l.id, l.style_id])), [levels]);
  const levelName = useMemo(() => Object.fromEntries(levels.map((l) => [l.id, l.name])), [levels]);
  const cls = classes.find((c) => c.id === form.class_id);
  const classStyleId = cls?.style_id || "";
  const classLevelId = cls?.level_id || "";

  const openNew = () => { setForm({ ...empty, sections: {} }); setLegacyView({}); setEditId(null); setDlg(true); };
  const openEdit = (p) => {
    const sections = {};
    const hasSections = p.sections && Object.keys(p.sections).length;
    SECTIONS.forEach(([k]) => {
      const s = (p.sections && p.sections[k]) || null;
      sections[k] = s ? { text: s.text || "", skill_ids: s.skill_ids || [] } : emptySection();
    });
    if (!hasSections && p.warmup) sections.warmup.text = p.warmup; // migrate legacy warm-up
    setForm({
      class_id: p.class_id || "", lesson_date: p.lesson_date || "", title: p.title || "",
      goals: p.goals || "", music: p.music || "", materials: p.materials || "",
      notes: p.notes || "", private_notes: p.private_notes || "", links: p.links || [], sections,
    });
    setLegacyView({ technique: p.technique || "", choreography: p.choreography || "", activities: p.activities || "" });
    setEditId(p.id); setDlg(true);
  };
  const setSection = (k, patch) => setForm((f) => ({ ...f, sections: { ...f.sections, [k]: { ...(f.sections[k] || emptySection()), ...patch } } }));

  const save = async () => {
    if (!form.class_id || !form.lesson_date) { toast.error("Pick a class and a date"); return; }
    try {
      if (editId) await api.put(`/lesson-plans/${editId}`, form);
      else await api.post("/lesson-plans", form);
      toast.success("Lesson plan saved"); setDlg(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (p) => { if (!window.confirm("Delete this lesson plan?")) return; try { await api.delete(`/lesson-plans/${p.id}`); toast.success("Deleted"); load(); } catch { toast.error("Error"); } };
  const copyTo = async (p) => { const d = window.prompt("Copy to which date? (YYYY-MM-DD)", p.lesson_date); if (!d) return; try { await api.post(`/lesson-plans/${p.id}/copy`, { lesson_date: d }); toast.success("Copied"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const recurring = async (p) => { const n = window.prompt("Create this plan for how many following weeks?", "4"); if (!n) return; try { const r = await api.post(`/lesson-plans/${p.id}/recurring`, { weeks: parseInt(n, 10) || 4 }); toast.success(`Created ${r.data.created} weekly plans`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const addComment = async () => { if (!comment.trim()) return; try { await api.post(`/lesson-plans/${view.id}/comment`, { text: comment }); toast.success("Comment added"); setComment(""); const r = await api.get(`/lesson-plans/${view.id}`); setView(r.data); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const addLink = () => { if (!linkUrl.trim()) return; setForm((f) => ({ ...f, links: [...(f.links || []), { label: linkUrl.trim(), url: linkUrl.trim() }] })); setLinkUrl(""); };

  const grouped = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return { upcoming: items.filter((p) => p.lesson_date >= today), past: items.filter((p) => p.lesson_date < today) };
  }, [items]);

  const Card = ({ p }) => (
    <div data-testid={`plan-${p.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start justify-between gap-3">
      <button className="min-w-0 text-left flex-1" onClick={() => setView(p)}>
        <p className="font-semibold">{p.title || "Untitled plan"}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{p.class_name} · {pretty(p.lesson_date)}</p>
        {p.goals && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{p.goals}</p>}
      </button>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full" title="Copy" data-testid={`plan-copy-${p.id}`} onClick={() => copyTo(p)}><Copy className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full" title="Weekly recurring" data-testid={`plan-recur-${p.id}`} onClick={() => recurring(p)}><Repeat className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full" data-testid={`plan-edit-${p.id}`} onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid={`plan-delete-${p.id}`} onClick={() => del(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );

  const vHasSections = view?.sections && Object.keys(view.sections).length;
  const vText = (k) => (view?.sections && view.sections[k]?.text) || (k === "warmup" && !vHasSections ? view?.warmup : "") || "";
  const vSkills = (k) => (view?.sections && view.sections[k]?.skills) || [];
  const editLegacy = LEGACY.filter(([k]) => (legacyView[k] || "").trim());

  return (
    <div className="stagger-in">
      <PageHeader title="Lesson Plans" subtitle="Plan your classes section by section and share ready plans with substitutes." testid="lessonplans-page"
        action={<Button data-testid="add-plan-button" onClick={openNew} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />New Plan</Button>} />

      <div className="flex flex-wrap gap-2 mb-5">
        <select data-testid="plan-filter-class" className="border rounded-full h-9 px-3 text-sm bg-white" value={filters.class_id} onChange={(e) => setFilters({ ...filters, class_id: e.target.value })}>
          <option value="">Any class</option>{classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" /><Input data-testid="plan-search" className="rounded-full h-9 pl-9 bg-white w-48" placeholder="Search…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} /></div>
      </div>

      {items.length === 0 ? <EmptyState icon={NotebookPen} title="No lesson plans yet" hint="Create a plan for a class and date." testid="plans-empty" /> :
        <div className="space-y-6">
          <div><h3 className="font-head font-bold mb-3">Upcoming</h3>
            <div className="space-y-3">{grouped.upcoming.length ? grouped.upcoming.map((p) => <Card key={p.id} p={p} />) : <p className="text-sm text-muted-foreground">Nothing upcoming.</p>}</div></div>
          {grouped.past.length > 0 && <div><h3 className="font-head font-bold mb-3">Past</h3><div className="space-y-3">{grouped.past.map((p) => <Card key={p.id} p={p} />)}</div></div>}
        </div>}

      {/* Editor */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><NotebookPen className="w-5 h-5 text-teal" />{editId ? "Edit" : "New"} Lesson Plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Class</Label>
                <select data-testid="plan-class" className="w-full border rounded-md h-10 px-3" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })}>
                  <option value="">Select…</option>{classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="space-y-1.5"><Label>Lesson date</Label><Input data-testid="plan-date" type="date" value={form.lesson_date} onChange={(e) => setForm({ ...form, lesson_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Title / focus</Label><Input data-testid="plan-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Center work + recital cleaning" /></div>
            <div className="space-y-1.5"><Label>Goals / objectives</Label><Textarea data-testid="plan-goals" rows={2} value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} /></div>

            <div className="rounded-2xl border border-gray-100 p-3 space-y-4">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Lesson sections</p>
              {SECTIONS.map(([k, label]) => (
                <div key={k} className="space-y-1.5" data-testid={`plan-section-block-${k}`}>
                  <Label>{label}</Label>
                  <Textarea data-testid={`plan-section-${k}`} rows={2} placeholder="Instructions (optional)…" value={form.sections[k]?.text || ""} onChange={(e) => setSection(k, { text: e.target.value })} />
                  <SkillPicker sectionKey={k} value={form.sections[k]?.skill_ids || []} onChange={(ids) => setSection(k, { skill_ids: ids })}
                    skills={skills} levelName={levelName} levelStyle={levelStyle} classStyleId={classStyleId} classLevelId={classLevelId} />
                </div>
              ))}
            </div>

            <div className="space-y-1.5"><Label>Music</Label><Textarea data-testid="plan-music" rows={2} value={form.music} onChange={(e) => setForm({ ...form, music: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Materials needed</Label><Textarea data-testid="plan-materials" rows={2} value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>General notes</Label><Textarea data-testid="plan-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Private teacher notes (not shown to subs)</Label><Textarea data-testid="plan-private" rows={2} value={form.private_notes} onChange={(e) => setForm({ ...form, private_notes: e.target.value })} /></div>

            {editLegacy.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="plan-legacy-block">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Previous lesson content (preserved)</p>
                {editLegacy.map(([k, label]) => (
                  <div key={k}><p className="text-xs font-semibold text-amber-800">{label}</p><p className="text-xs text-amber-800/80 whitespace-pre-wrap">{legacyView[k]}</p></div>
                ))}
                <p className="text-[11px] text-amber-700/80">This content is kept from an earlier version of this plan and stays saved as-is.</p>
              </div>
            )}

            <div className="space-y-1.5"><Label>Links</Label>
              <div className="flex gap-2"><Input data-testid="plan-link-input" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" /><Button type="button" variant="outline" className="rounded-full" onClick={addLink}>Add</Button></div>
              {(form.links || []).map((l, i) => <p key={i} className="text-xs text-teal-dark truncate">🔗 {l.url}</p>)}
            </div>
          </div>
          <DialogFooter><Button data-testid="save-plan-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer */}
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{view?.title || "Lesson Plan"}</DialogTitle></DialogHeader>
          {view && <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">{view.class_name} · {pretty(view.lesson_date)} · {view.teacher_name}</p>
            {view.goals && <div><p className="font-semibold">Goals / objectives</p><p className="whitespace-pre-wrap text-muted-foreground">{view.goals}</p></div>}
            {SECTIONS.map(([k, label]) => {
              const text = vText(k); const sk = vSkills(k);
              if (!text && !sk.length) return null;
              return <div key={k} data-testid={`plan-view-section-${k}`}>
                <p className="font-semibold">{label}</p>
                {text && <p className="whitespace-pre-wrap text-muted-foreground">{text}</p>}
                {sk.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1">{sk.map((s) => <span key={s.id} data-testid={`plan-view-skill-${s.id}`} className="text-xs bg-teal-light text-teal-dark rounded-full px-2.5 py-0.5 font-semibold">{s.name}</span>)}</div>}
              </div>;
            })}
            {["music", "materials", "notes"].map((k) => view[k] ? <div key={k}><p className="font-semibold capitalize">{k === "notes" ? "General notes" : k}</p><p className="whitespace-pre-wrap text-muted-foreground">{view[k]}</p></div> : null)}
            {view.private_notes && <div className="rounded-lg bg-yellow-50 p-2.5" data-testid="plan-view-private"><p className="font-semibold text-yellow-800">Private notes</p><p className="whitespace-pre-wrap text-yellow-700">{view.private_notes}</p></div>}
            {LEGACY.some(([k]) => view[k]) && <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid="plan-view-legacy">
              <p className="font-semibold text-amber-800">Previous lesson content</p>
              {LEGACY.map(([k, label]) => view[k] ? <div key={k} className="mt-1"><p className="text-xs font-semibold text-amber-800">{label}</p><p className="whitespace-pre-wrap text-amber-800/80">{view[k]}</p></div> : null)}
            </div>}
            {(view.links || []).map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" className="text-teal-dark font-semibold flex items-center gap-1.5"><ExternalLink className="w-4 h-4" />{l.url}</a>)}
            <div className="border-t pt-3">
              <p className="font-semibold flex items-center gap-1.5 mb-2"><MessageSquare className="w-4 h-4 text-teal" />Director comments</p>
              {(view.comments || []).length === 0 ? <p className="text-muted-foreground text-xs">No comments yet.</p> :
                (view.comments || []).map((c, i) => <div key={i} className="bg-background rounded-lg p-2 mb-1.5"><p className="text-xs font-semibold">{c.name}</p><p className="text-sm">{c.text}</p></div>)}
              {isDirector && <div className="flex gap-2 mt-2"><Input data-testid="plan-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" /><Button data-testid="plan-comment-add" className="rounded-full bg-teal hover:bg-teal-dark" onClick={addComment}>Send</Button></div>}
            </div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
